import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import {
  CreateHealthUnit,
  DeactiveHealthUnit,
  FiltersHealthUnits
} from '../Types/Requests/HealthUnitRequests'
import {
  HealthUnitDetailResponse,
  HealthUnitResponse,
  ListHealthUnitsResponse,
  SimpleHealthUnitResponse
} from '../Types/Responses/HealthUnitResponses'
import type { HealthUnitSelectOption } from '@/pinia-store/useHealthUnitStore'

/** Payload só com `isActive` = ativar/desativar; o resto é a edição do formulário. */
function updateFallback(payload: CreateHealthUnit | DeactiveHealthUnit): string {
  const keys = Object.keys(payload)
  if (keys.length === 1 && keys[0] === 'isActive') {
    return (payload as DeactiveHealthUnit).isActive
      ? 'Não foi possível ativar a unidade.'
      : 'Não foi possível desativar a unidade.'
  }
  return 'Não foi possível salvar as alterações da unidade.'
}

export default class HealthUnitService extends Service {
  prefix: string

  constructor() {
    super()

    this.prefix = '/healthUnits'
  }

  async listAdminAccess(): Promise<HealthUnitSelectOption[]> {
    try {
      const { data } = await request.get<HealthUnitSelectOption[]>(
        `${this.getCompletePath()}/list-admin`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as unidades que você administra.')
    }
  }

  async listViewAccess(): Promise<HealthUnitSelectOption[]> {
    try {
      const { data } = await request.get<HealthUnitSelectOption[]>(
        `${this.getCompletePath()}/list-view`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as unidades.')
    }
  }

  async show(healthUnitId: number): Promise<HealthUnitDetailResponse> {
    try {
      const { data } = await request.get<HealthUnitDetailResponse>(
        `${this.getCompletePath()}/${healthUnitId}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os dados da unidade.')
    }
  }

  async create(companyId: number, payload: CreateHealthUnit): Promise<HealthUnitResponse> {
    try {
      const response = await request.post<HealthUnitResponse>(
        `${this.getCompletePath()}/company/${companyId}`,
        payload
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível cadastrar a unidade.')
    }
  }

  /**
   * O mesmo PATCH serve à edição do formulário e à ativação/desativação
   * (`{ isActive }` sozinho), então o fallback diz qual das duas falhou.
   */
  async update(
    healthUnitId: number,
    payload: CreateHealthUnit | DeactiveHealthUnit
  ): Promise<void> {
    try {
      await request.patch(`${this.getCompletePath()}/${healthUnitId}`, payload)
    } catch (e) {
      throw this.handleError(e, updateFallback(payload))
    }
  }

  // Sem fallback silencioso de propósito: engolir o erro e devolver
  // `{count: 0, data: []}` transformava falha de rede ou 403 em "nenhuma unidade
  // encontrada", o que mascarava o problema real. O catch só normaliza a
  // mensagem — quem chama trata (o store tem isError/errorMessage).
  async list(filters?: FiltersHealthUnits): Promise<ListHealthUnitsResponse> {
    try {
      const { data } = await request.get<ListHealthUnitsResponse>(this.getCompletePath(), filters)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as unidades.')
    }
  }

  async updateAvatar(healthUnitId: number, file: File): Promise<{ avatarUrl: string }> {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const { data } = await request.put<{ avatarUrl: string }>(
        this.getCompletePath(`/${healthUnitId}/avatar`),
        formData,
        { 'Content-Type': 'multipart/form-data' }
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar a foto da unidade.')
    }
  }

  async listV2(
    filters?: FiltersHealthUnits
  ): Promise<{ data: SimpleHealthUnitResponse[]; count: number }> {
    const page = (filters?.page || 1) - 1
    const limit = filters?.limit || 20
    const offset = page * limit
    if (filters) filters.offset = offset

    try {
      const { data } = await request.get<{ data: SimpleHealthUnitResponse[]; count: number }>(
        this.getCompletePath(),
        filters
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as unidades.')
    }
  }
}
